import { Landmark, ShieldCheck, Crown } from "lucide-react";
import { accountantNavMain, accountantNavSecondary } from "@/lib/navigation";
import { canManageRefundsUser } from "@/lib/auth";
import { NavMain } from "@/components/navigation/nav-main";
import { NavSecondary } from "@/components/navigation/nav-secondary";
import { NavUser } from "@/components/navigation/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import type { AuthUser } from "@/types/auth";

export function AccountantSidebar({ user }: { user: AuthUser }) {
  const isFounder = user.role === "FOUNDER";

  return (
    <Sidebar collapsible="offcanvas" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className={`group relative overflow-hidden flex items-center gap-3 rounded-xl border p-3 shadow-xs backdrop-blur-md transition-all duration-300 hover:shadow-sm ${
              isFounder 
                ? "border-amber-500/25 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-500/40" 
                : "border-sidebar-border/50 bg-sidebar-accent/30 hover:bg-sidebar-accent/50 hover:border-sidebar-border/85"
            }`}>
              {/* Subtle background glow/glass effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 opacity-50 pointer-events-none" />
              
              <div className={`flex size-10 shrink-0 items-center justify-center rounded-xl border shadow-2xs transition-transform duration-300 group-hover:scale-105 ${
                isFounder
                  ? "bg-gradient-to-tr from-amber-500/20 to-yellow-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30"
                  : "bg-gradient-to-tr from-emerald-500/20 to-teal-500/10 text-brand dark:text-teal-400 border-teal-500/30"
              }`}>
                {isFounder ? (
                  <Crown className="size-5" />
                ) : (
                  <Landmark className="size-5" />
                )}
              </div>
              
              <div className="flex flex-col relative z-10">
                <span className={`text-sm font-semibold leading-tight transition-colors duration-300 ${
                  isFounder
                    ? "bg-gradient-to-r from-amber-600 to-yellow-500 dark:from-amber-400 dark:to-yellow-300 bg-clip-text text-transparent group-hover:from-amber-500 group-hover:to-yellow-400"
                    : "text-sidebar-foreground group-hover:text-brand"
                }`}>
                  {isFounder ? "Landnest Executive" : "Landnest Finance"}
                </span>
                <span className="text-sidebar-foreground/70 text-xs leading-tight font-medium mt-0.5 transition-colors duration-300 group-hover:text-sidebar-foreground/90">
                  {isFounder ? "Founder Workspace" : "Accounting Workspace"}
                </span>
              </div>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <div className="px-2 pt-2">
          <NavUser user={user} />
        </div>
        <NavMain items={accountantNavMain.filter((item) => item.url !== "/admin/refunds" || canManageRefundsUser(user))} />
        <NavSecondary items={accountantNavSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <div className="text-sidebar-foreground/70 flex items-center gap-2 rounded-lg border border-sidebar-border/70 bg-sidebar-accent/60 px-3 py-2 text-xs">
          <ShieldCheck className="size-4" />
          Finance routes still enforce the same backend RBAC rules
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
